import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Download, FileText, Search, Pencil, Printer, Trash2, Mail, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LetterEditor } from "./LetterEditor";
import { printDecision, decisionPDFBlob } from "../lib/decisionPrint";
import { useBulkSelect, RowCheck, BulkBar } from "./common/BulkSelect";

// Saved letterhead documents (free text on company letterhead)
export const LettersList = ({ employees, edit, setEdit }) => {
  const [q, setQ] = useState("");
  const { company } = useAuth();
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.listLetters().then((r) => setItems(r.items || [])).catch(() => toast.error("تعذّر تحميل المستندات")), []);
  useEffect(() => { load(); }, [load]);
  const del = async (d) => {
    if (!window.confirm(`حذف المستند ${d.number}؟`)) return;
    try { await api.deleteLetter(d.id); toast.success("تم الحذف"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };
  const pdf = async (d) => {
    try { const b = await decisionPDFBlob({ ...d, kind: "letter" }, company, fileUrl); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `${(d.title || d.number).slice(0, 50).replace(/\//g, "-")}.pdf`; a.click(); }
    catch { toast.error("تعذّر إنشاء PDF"); }
  };
  const [mailing, setMailing] = useState("");
  const email = async (d) => {
    if (!d.recipient_email) { toast.warning("أضف بريد الجهة المستلمة في الخطاب أولاً (زر التعديل)"); setEdit(d); return; }
    if (!window.confirm(`إرسال الخطاب ${d.number} إلى ${d.recipient_email}؟`)) return;
    setMailing(d.id);
    try { const r = await api.emailLetter(d.id, await decisionPDFBlob({ ...d, kind: "letter" }, company, fileUrl)); toast.success(`تم الإرسال إلى ${r.to}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); } finally { setMailing(""); }
  };
  const s = (q || "").trim();
  const list = items.filter((d) => !s || [d.title, d.number, (d.body_html || "").replace(/<[^>]+>/g, " ")].some((x) => (x || "").includes(s)));
  const bulk = useBulkSelect();
  return (
    <div className="space-y-3">
    <div className="relative"><Search className="absolute right-3 top-2.5 h-4 w-4 text-slate-400" />
      <Input data-testid="letters-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالعنوان أو الرقم أو النص…" className="pr-9" /></div>
    {list.length > 0 && <BulkBar bulk={bulk} allIds={list.map((d) => d.id)} noun="مستند" prefix="letters-bulk"
      onDelete={async (ids) => { const r = await api.lettersBulkDelete(ids); load(); return r; }} />}
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="letters-list">
      {list.length === 0 ? <p className="p-10 text-center text-sm text-slate-400" data-testid="letters-empty">لا توجد مستندات بعد — ابدأ بـ «مستند بكليشة الشركة»</p>
        : list.map((d) => (
          <div key={d.id} data-testid={`letter-row-${d.id}`} className={`flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0 hover:bg-slate-50/70 ${bulk.sel.has(d.id) ? "bg-emerald-50/60" : ""}`}>
            <RowCheck checked={bulk.sel.has(d.id)} onChange={() => bulk.toggle(d.id)} testid={`letter-check-${d.id}`} />
            <FileText className="h-5 w-5 shrink-0 text-indigo-500" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-slate-800">{d.title || "مستند بدون عنوان"} <span className="text-xs font-normal text-slate-400">#{d.number}</span></p>
              <p className="truncate text-xs text-slate-500">{d.date} · {d.created_by}{d.recipient_name ? ` · إلى: ${d.recipient_name}` : ""}{d.emailed_at ? ` · أُرسل بالبريد ${d.emailed_at.slice(0, 10)}` : ""}</p>
            </div>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" title="تنزيل PDF (ويمكن إرساله واتساب)" data-testid={`letter-pdf-${d.id}`} onClick={() => pdf(d)}><Download className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" title="إرسال بالبريد للجهة" data-testid={`letter-email-${d.id}`} disabled={mailing === d.id} onClick={() => email(d)} className="text-sky-600">{mailing === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}</Button>
              <Button size="icon" variant="ghost" data-testid={`letter-print-${d.id}`} onClick={() => printDecision({ ...d, kind: "letter" }, company, fileUrl)}><Printer className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" data-testid={`letter-edit-${d.id}`} onClick={() => setEdit(d)}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" data-testid={`letter-delete-${d.id}`} onClick={() => del(d)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>))}
    </div>
      {edit && <LetterEditor key={edit.id || "new"} letter={edit.id ? edit : null} employees={employees} onOpenChange={(o) => !o && setEdit(null)} onSaved={load} />}
    </div>
  );
};
